/* ==========================================================================
   A crash should cost somebody one tap, not their whole session.

   Without this, one bad render blanks the screen and a tester assumes the app
   is broken and stops. With it they see what happened, can get back to a
   working screen, and can tell us — which is the entire point of a test.
   ========================================================================== */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { t } from '../lib/i18n';

interface Props {
  children: ReactNode;
  /** Changing this resets the boundary — used to recover on navigation. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ozeret] screen crashed:', error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="screen-pad" style={{ paddingTop: 40 }}>
        <div className="empty">
          <div className="big">{t('e_hit')}</div>
          <p className="sub">
            {t('e_notyou')}
          </p>
        </div>

        <button
          type="button"
          className="btn block lg"
          onClick={() => this.setState({ error: null })}
        >
          {t('e_retry')}
        </button>
        <button
          type="button"
          className="btn ghost block"
          onClick={() => { window.location.href = '/'; }}
        >
          {t('e_home')}
        </button>

        <details className="note" style={{ marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 700 }}>
            {t('e_what')}
          </summary>
          <p className="mono" style={{ fontSize: 12, marginTop: 8, wordBreak: 'break-word' }}>
            {error.message}
          </p>
        </details>
      </div>
    );
  }
}
